/**
 * Whether a click on a link should start a login here instead of travelling to `/auth/login`.
 *
 * Pure functions of an href and a click's shape, so all three are exported and tested. The DOM
 * half -- finding the anchor, cancelling the event, marking it busy -- is wiring and lives in
 * ./index.js.
 *
 * WHY THIS EXISTS
 * ---------------
 * `/auth/login` is a shell page whose only job is to load this bundle and call `login()`. A fan
 * who clicks the header's sign-in button therefore pays for a full document plus a 62 KB module
 * download *before* anything starts happening, and looks at an interstitial while they do. On the
 * page they are already on, this bundle is loaded and evaluated, so calling `login()` from the
 * click sends them straight to Auth0 -- one fewer document, one fewer wait, and one fewer screen.
 *
 * **The route stays and the anchors stay anchors.** This is an enhancement layered over a link
 * that already works, and the link is the fallback for every case this cannot serve: a click that
 * lands before the deferred module evaluates, `<noscript>`, a copied or bookmarked URL, an install
 * with no built bundle, and cached HTML still pointing at a pre-change bundle. Replacing the
 * anchors with buttons would give all of that up -- see resources/scripts/loyalty/loginUrl.js,
 * which makes the same argument about the same control from the other end.
 *
 * WHY THE HREF IS COMPARED LIVE RATHER THAN BOUND ONCE
 * ---------------------------------------------------
 * The header's inline reconcile rewrites those anchors' `href` on every `urc-auth:changed` --
 * to the external My URC URL for a signed-in fan and back to the sign-in URL on sign-out. So the
 * question "is this a login link" has a different answer at different moments for the same
 * element, and only a matcher that reads the href at click time gets it right in both directions.
 * A listener bound per-anchor at load would intercept a My URC click and would miss an anchor that
 * became a sign-in link later. See docs/subsystems/header.md.
 */

/**
 * Whether two URLs address the same route.
 *
 * Origin plus path, with a trailing slash ignored -- `App\Auth\Routes::action()` trims the path
 * before its lookup, so both spellings reach the same shell, while `Auth0Config::url()` advertises
 * the bare one. The query string is deliberately not compared: `return_to` is the whole reason
 * these hrefs differ from one another.
 *
 * **Never substring-match `/auth/login`.** It also matches `/auth/login-help`, and a subdirectory
 * install serves the route under a prefix, so the literal is wrong in both directions.
 *
 * @param {string} href     The anchor's href. Absolute, as the DOM property always is.
 * @param {string} loginUrl The login route, from the published config.
 * @return {boolean} Whether the href is that route.
 */
export function isLoginHref( href, loginUrl ) {
	if ( typeof href !== 'string' || typeof loginUrl !== 'string' || loginUrl === '' ) {
		return false;
	}

	let target;
	let route;

	try {
		target = new URL( href );
		route = new URL( loginUrl );
	} catch ( error ) {
		return false;
	}

	if ( target.origin !== route.origin ) {
		return false;
	}

	const path = value => value.replace( /\/+$/, '' );

	return path( target.pathname ) === path( route.pathname );
}

/**
 * The `return_to` an anchor is asking for, or ''.
 *
 * Passed through rather than improved. The three header controls carry none on purpose -- a
 * sign-in started there lands the fan on the home page, and `safeReturnTo( '', homeUrl )` answers
 * exactly that -- while the loyalty join CTA carries the page it was clicked on. Substituting
 * `window.location.href` for the empty case would silently change documented behaviour for three
 * controls. It is validated downstream, by ./returnTo.js, as any value from outside this bundle is.
 *
 * @param {string} href The anchor's href.
 * @return {string} The requested return URL, unvalidated, or ''.
 */
export function returnToFrom( href ) {
	if ( typeof href !== 'string' ) {
		return '';
	}

	try {
		return new URL( href ).searchParams.get( 'return_to' ) || '';
	} catch ( error ) {
		return '';
	}
}

/**
 * Whether a click is the kind that means "go to this page, in this tab".
 *
 * Everything else is left to the browser, and that is what keeps the anchor an anchor: a
 * ctrl/cmd-click opens the shell in a new tab and the shell works, `target="_blank"` is honoured,
 * and an event another handler has already claimed is not claimed twice. Middle-click does not
 * reach a `click` listener in current browsers -- it fires `auxclick` -- and `button` is checked
 * anyway rather than relying on that.
 *
 * Takes a plain object rather than the event so this stays pure; ./index.js assembles it from the
 * event and the anchor.
 *
 * @param {Object}  click                  The click's shape.
 * @param {number}  click.button           Which button. 0 is the primary one.
 * @param {boolean} click.metaKey
 * @param {boolean} click.ctrlKey
 * @param {boolean} click.shiftKey
 * @param {boolean} click.altKey
 * @param {boolean} click.defaultPrevented Whether something else has already handled it.
 * @param {string}  click.target           The anchor's `target` attribute.
 * @return {boolean} Whether to take it over.
 */
export function shouldIntercept( click ) {
	const it = click || {};

	if ( it.button !== 0 || it.defaultPrevented ) {
		return false;
	}

	if ( it.metaKey || it.ctrlKey || it.shiftKey || it.altKey ) {
		return false;
	}

	const target = typeof it.target === 'string' ? it.target.trim().toLowerCase() : '';

	return target === '' || target === '_self';
}
